import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth-context";

// ============================================================
// STORES
// ============================================================
export function useStores() {
  return useQuery({
    queryKey: ["stores"],
    queryFn: async () => {
      const { data, error } = await supabase.from("stores").select("*").order("name");
      if (error) throw error;
      return data ?? [];
    },
  });
}

// ============================================================
// BENEFIT DEFINITIONS
// ============================================================
export type BenefitDefinition = {
  id: string;
  name: string;
  description: string | null;
  icon: string;
  category: string; // 'unlimited' | 'limited' | 'extra'
  is_limited: boolean;
  limit_per_period: number;
  sort_order: number;
  active: boolean;
  activation_type?: "booking" | "in_store";
  period_days?: number;
  display_only?: boolean;
};

export function useBenefitDefinitions(opts: { onlyActive?: boolean } = {}) {
  const { onlyActive = false } = opts;
  return useQuery({
    queryKey: ["benefit_definitions", onlyActive],
    queryFn: async () => {
      let q = supabase.from("benefit_definitions" as any).select("*").order("sort_order");
      if (onlyActive) q = q.eq("active", true);
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as unknown as BenefitDefinition[];
    },
  });
}

export function useBenefitMutations() {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: ["benefit_definitions"] });
  return {
    create: useMutation({
      mutationFn: async (b: Partial<BenefitDefinition>) => {
        const { error } = await supabase.from("benefit_definitions" as any).insert(b as any);
        if (error) throw error;
      },
      onSuccess: invalidate,
    }),
    update: useMutation({
      mutationFn: async (b: Partial<BenefitDefinition> & { id: string }) => {
        const { id, ...rest } = b;
        const { error } = await supabase.from("benefit_definitions" as any).update(rest as any).eq("id", id);
        if (error) throw error;
      },
      onSuccess: invalidate,
    }),
    remove: useMutation({
      mutationFn: async (id: string) => {
        const { error } = await supabase.from("benefit_definitions" as any).delete().eq("id", id);
        if (error) throw error;
      },
      onSuccess: invalidate,
    }),
  };
}

// ============================================================
// PARTNERS
// ============================================================
export type Partner = {
  id: string;
  name: string;
  description: string | null;
  discount_label: string | null;
  terms: string | null;
  logo_url: string | null;
  category: string | null;
  cta_label: string | null;
  cta_url: string | null;
  active: boolean;
  valid_until: string | null;
  sort_order: number;
};

export function usePartners(opts: { onlyActive?: boolean } = {}) {
  const { onlyActive = false } = opts;
  return useQuery({
    queryKey: ["partners", onlyActive],
    queryFn: async () => {
      let q = supabase.from("partners").select("*").order("sort_order");
      if (onlyActive) q = q.eq("active", true);
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as Partner[];
    },
  });
}

export function usePartnerMutations() {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: ["partners"] });
  return {
    create: useMutation({
      mutationFn: async (p: Partial<Partner>) => {
        const { error } = await supabase.from("partners").insert(p as any);
        if (error) throw error;
      },
      onSuccess: invalidate,
    }),
    update: useMutation({
      mutationFn: async (p: Partial<Partner> & { id: string }) => {
        const { id, ...rest } = p;
        const { error } = await supabase.from("partners").update(rest as any).eq("id", id);
        if (error) throw error;
      },
      onSuccess: invalidate,
    }),
    remove: useMutation({
      mutationFn: async (id: string) => {
        const { error } = await supabase.from("partners").delete().eq("id", id);
        if (error) throw error;
      },
      onSuccess: invalidate,
    }),
  };
}

// ============================================================
// EVENTS
// ============================================================
export type EventRow = {
  id: string;
  title: string;
  description: string | null;
  store_id: string | null;
  starts_at: string;
  ends_at: string | null;
  max_attendees: number;
  cover_image_url: string | null;
  active: boolean;
};

export function useEvents(opts: { onlyActive?: boolean; upcomingOnly?: boolean } = {}) {
  const { onlyActive = false, upcomingOnly = false } = opts;
  return useQuery({
    queryKey: ["events", onlyActive, upcomingOnly],
    queryFn: async () => {
      let q = supabase.from("events").select("*").order("starts_at");
      if (onlyActive) q = q.eq("active", true);
      if (upcomingOnly) q = q.gte("starts_at", new Date().toISOString());
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as EventRow[];
    },
  });
}

export function useEventMutations() {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: ["events"] });
  return {
    create: useMutation({
      mutationFn: async (e: Partial<EventRow>) => {
        const { data, error } = await supabase.from("events").insert(e as any).select().single();
        if (error) throw error;
        return data as EventRow;
      },
      onSuccess: invalidate,
    }),

    update: useMutation({
      mutationFn: async (e: Partial<EventRow> & { id: string }) => {
        const { id, ...rest } = e;
        const { error } = await supabase.from("events").update(rest as any).eq("id", id);
        if (error) throw error;
      },
      onSuccess: invalidate,
    }),
    remove: useMutation({
      mutationFn: async (id: string) => {
        const { error } = await supabase.from("events").delete().eq("id", id);
        if (error) throw error;
      },
      onSuccess: invalidate,
    }),
  };
}

// Event registrations for a single event (with member info, staff use)
export function useEventRegistrations(eventId: string | null) {
  const { session } = useAuth();
  return useQuery({
    queryKey: ["event_registrations", eventId],
    enabled: !!eventId && !!session,
    queryFn: async () => {
      if (!eventId) return [];
      const { data, error } = await supabase
        .from("event_registrations")
        .select("id, status, checked_in_at, created_at, member:members(id, member_number, first_name, last_name, email, phone)")
        .eq("event_id", eventId)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
  });
}

// All registrations for a single member (event info included)
export function useMemberEventRegistrations(memberId: string | null) {
  const { session } = useAuth();
  return useQuery({
    queryKey: ["member_event_registrations", memberId],
    enabled: !!memberId && !!session,
    queryFn: async () => {
      if (!memberId) return [];
      const { data, error } = await supabase
        .from("event_registrations")
        .select("id, status, checked_in_at, created_at, event:events(id, title, starts_at, store_id)")
        .eq("member_id", memberId)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
  });
}

// All benefit logs for a member (activations and redemptions)
export function useMemberBenefitLogs(memberId: string | null) {
  const { session } = useAuth();
  return useQuery({
    queryKey: ["member_benefit_logs", memberId],
    enabled: !!memberId && !!session,
    queryFn: async () => {
      if (!memberId) return [];
      const { data, error } = await supabase
        .from("benefit_logs")
        .select("id, benefit_label, activation_code, activated_at, redeemed_at, used_at, expires_at, booking_at, booking_message, store_id")
        .eq("member_id", memberId)
        .order("activated_at", { ascending: false, nullsFirst: false });
      if (error) throw error;
      return data ?? [];
    },
  });
}

// ============================================================
// MEMBERS
// ============================================================
export type Member = {
  id: string;
  member_number: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string | null;
  address: string | null;
  zip: string | null;
  city: string | null;
  ssn: string | null;
  birth_date: string | null;
  gender: string | null;
  store_id: string | null;
  status: string;
  operator: string | null;
  phone_brand: string | null;
  phone_model: string | null;
  points: number;
  member_since: string;
  created_at: string;
  interests: string[];
  interests_text: string | null;
  source: string | null;
  customer_type: string | null;
  notes: string | null;
  gdpr_consent: boolean;
  stripe_customer_id?: string | null;
  stripe_subscription_id?: string | null;
  subscription_status?: string | null;
  current_period_end?: string | null;
  cancel_at?: string | null;
  cancelled_at?: string | null;
  binding_until?: string | null;
  referral_code?: string | null;
};

export function useMembers(opts: { includeDeleted?: boolean } = {}) {
  const { session } = useAuth();
  return useQuery({
    queryKey: ["members", opts.includeDeleted ? "all" : "active"],
    enabled: !!session,
    queryFn: async () => {
      let q = supabase
        .from("members")
        .select("*")
        .order("created_at", { ascending: false });
      if (!opts.includeDeleted) {
        q = q.is("deleted_at", null);
      }
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as Member[];
    },
  });
}

export function useMemberMutations() {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: ["members"] });
  return {
    create: useMutation({
      mutationFn: async (m: Partial<Member>) => {
        const { data, error } = await supabase.from("members").insert(m as any).select().single();
        if (error) throw error;
        return data as Member;
      },
      onSuccess: invalidate,
    }),
    update: useMutation({
      mutationFn: async (m: Partial<Member> & { id: string }) => {
        const { id, ...rest } = m;
        const { error } = await supabase.from("members").update(rest as any).eq("id", id);
        if (error) throw error;
      },
      onSuccess: invalidate,
    }),
    // Hard delete: removes member, auth user, and cancels Stripe subscription.
    // Frees up the email so it can be registered again.
    remove: useMutation({
      mutationFn: async (args: { id: string; reason?: string }) => {
        const { data, error } = await supabase.functions.invoke("delete-member-fully", {
          body: { member_id: args.id, reason: args.reason ?? null },
        });
        if (error) throw error;
        if ((data as any)?.error) throw new Error((data as any).error);
      },
      onSuccess: invalidate,
    }),

    restore: useMutation({
      mutationFn: async (id: string) => {
        const { error } = await supabase.rpc("restore_member" as any, { _member_id: id });
        if (error) throw error;
      },
      onSuccess: invalidate,
    }),
    setStatus: useMutation({
      mutationFn: async ({ id, status }: { id: string; status: string }) => {
        const { error } = await supabase.from("members").update({ status: status as any }).eq("id", id);
        if (error) throw error;
      },
      onSuccess: invalidate,
    }),
  };
}

// ============================================================
// Benefit usage logs — used to know if a member has used a
// limited benefit during the current calendar month
// ============================================================
export function useBenefitUsageThisMonth(memberId: string | null) {
  const { session } = useAuth();
  return useQuery({
    queryKey: ["benefit_logs_month", memberId],
    enabled: !!memberId && !!session,
    queryFn: async () => {
      if (!memberId) return [];
      const firstOfMonth = new Date();
      firstOfMonth.setDate(1);
      firstOfMonth.setHours(0, 0, 0, 0);
      const { data, error } = await supabase
        .from("benefit_logs")
        .select("*")
        .eq("member_id", memberId)
        .gte("used_at", firstOfMonth.toISOString());
      if (error) throw error;
      return data ?? [];
    },
  });
}

// Returns first day of next month as a Date.
export function firstOfNextMonth(from: Date = new Date()) {
  const d = new Date(from);
  d.setMonth(d.getMonth() + 1);
  d.setDate(1);
  d.setHours(0, 0, 0, 0);
  return d;
}

export function formatNextResetLabel(from: Date = new Date()) {
  const d = firstOfNextMonth(from);
  return `Återställs den 1:a ${d.toLocaleDateString("sv-SE", { month: "long" })}`;
}

// ============================================================
// Current member — read from Supabase Auth + members table
// ============================================================
export function useCurrentMember(): Member | null {
  const { user } = useAuth();
  const { data } = useQuery({
    queryKey: ["current_member", user?.id],
    enabled: !!user?.id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("members")
        .select("*")
        .eq("user_id", user!.id)
        .is("deleted_at", null)
        .maybeSingle();
      if (error) throw error;
      return (data as Member | null) ?? null;
    },
  });
  return data ?? null;
}

// === Eventnärvaro ("antal kvällar") ===

/** Personal-gate:ad: antal incheckade kvällar per medlem (alla medlemmar). */
export function useAttendanceCounts(enabled = true) {
  const { session } = useAuth();
  return useQuery({
    queryKey: ["attendance-counts"],
    enabled: enabled && !!session,
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_members_attendance_counts" as any);
      if (error) throw error;
      const map = new Map<string, number>();
      ((data ?? []) as { member_id: string; nights: number }[]).forEach((r) =>
        map.set(r.member_id, Number(r.nights) || 0)
      );
      return map;
    },
  });
}

/** Inloggad medlems egna antal kvällar. */
export function useMyAttendanceCount() {
  const { session } = useAuth();
  const { data } = useQuery({
    queryKey: ["my-attendance-count"],
    enabled: !!session,
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_my_attendance_count" as any);
      if (error) throw error;
      return Number(data ?? 0);
    },
  });
  return data ?? 0;
}
